<template>
  <router-link
    :to="{ name: 'talent-pool-shift-detail', params: { shiftId: props.shift.shift_id, locationId, tab: 'today' } }"
    class="shift-container"
  >
    <div :class="['shift', { completed, starting: startingNow, done: signOff, overdue: signOffOverdue }]">
      <div class="header">
        <div>
          <transition v-if="altHeadingText" mode="out-in" name="heading-toggle">
            <span class="heading" :key="toggleHeading">{{ currentHeading }}</span>
          </transition>
          <div v-else class="heading">{{ props.shift.winning_claim_employee_name }}</div>
          <div class="role">{{ props.shift.portal_role_name }}</div>
        </div>
        <svg v-if="completed" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5ZM11.5017 5.87648C11.7771 6.16508 11.7663 6.62224 11.4777 6.89757L7.50322 10.6892C7.224 10.9556 6.7847 10.9554 6.50571 10.6888L4.52186 8.79298C4.23349 8.5174 4.22312 8.06024 4.49869 7.77186C4.77427 7.4835 5.23144 7.47312 5.51981 7.7487L7.00511 9.1681L10.4806 5.85244C10.7692 5.57711 11.2264 5.58787 11.5017 5.87648Z"
            fill="#637488"
          />
        </svg>

        <EmployeeAvatar v-else :imageUrl="props.shift.winning_claim_employee_photo_url" :size="24" />
      </div>
      <b-progress :max="100" height="4px">
        <b-progress-bar :value="percentage" class="todos__progress-bar" />
      </b-progress>
      <div v-if="inProgress && !signOff" class="times">
        <span class="start">{{ formatTime(props.shift.start_at) }}</span>
        <span class="end">{{ formatTime(props.shift.end_at) }}</span>
      </div>
      <div v-else class="action">
        <span>{{ actionText }}</span>
      </div>
    </div>
    <div v-if="!props.last" class="line" />
  </router-link>
</template>

<script setup>
import { useRoute } from 'vue-router/composables';
import EmployeeAvatar from '@/pages/talent-pool/components/EmployeeAvatar.vue';
import { computed, ref, watchEffect } from 'vue';
import { formatTime, formatTimeRemaining } from '@/pages/talent-pool/utils/datetime';
import { addMinutes, isFuture, isPast, subMinutes } from 'date-fns';

const route = useRoute();
const locationId = route.params.locationId;

const props = defineProps(['shift']);

const toggleHeading = ref(true);

const inProgress = computed(() => {
  return isPast(props.shift.start_at) && isFuture(props.shift.end_at);
});

const completed = computed(() => {
  return props.shift.most_relevant_claim_status === 'FULFILLED';
});
const startingNow = computed(() => {
  return props.shift.most_relevant_claim_status === 'APPROVED' && subMinutes(props.shift.start_at, 15) < new Date();
});
const signOff = computed(() => {
  return props.shift.most_relevant_claim_status === 'APPROVED' && subMinutes(props.shift.end_at, 15) < new Date();
});
const signOffOverdue = computed(() => {
  return isPast(addMinutes(props.shift.end_at, 10)) && !completed.value;
});
const actionText = computed(() => {
  if (completed.value) {
    return `Signed-off at ${formatTime(props.shift.claim_properties.fulfilled_at)}`;
  }
  if (signOffOverdue.value) {
    return 'Sign-off to finalise';
  }
  if (signOff.value) {
    return 'Scan their QR code to sign-off';
  }
  if (startingNow.value) {
    return 'Scan their QR code to start';
  }
  return `Starts at ${formatTime(props.shift.start_at)}`;
});

const altHeadingText = computed(() => {
  if (signOffOverdue.value) {
    return `Finished at ${formatTime(props.shift.end_at)}`;
  }
  if (signOff.value) {
    return `Finishes at ${formatTime(props.shift.end_at)}`;
  }
  if (inProgress.value) {
    return `${formatTimeRemaining(props.shift.end_at)} remaining`;
  }
  if (startingNow.value) {
    return `Starts at ${formatTime(props.shift.start_at)}`;
  }
  return null;
});

const currentHeading = computed(() =>
  toggleHeading.value ? props.shift.winning_claim_employee_name : altHeadingText.value,
);

const percentage = computed(() => {
  const start = new Date(props.shift.start_at);
  const end = new Date(props.shift.end_at);
  const now = new Date();

  const total = end - start;
  const elapsed = now - start;

  return Math.min(100, (elapsed / total) * 100);
});

watchEffect(onInvalidate => {
  const timer = setInterval(() => {
    toggleHeading.value = !toggleHeading.value;
  }, 4000);

  onInvalidate(() => {
    clearInterval(timer);
  });
});
</script>

<style scoped lang="scss">
.shift-container {
  min-width: 200px;
}

.shift {
  padding: 1rem 12px;
  background: var(--palette-color-base-white);
  display: flex;
  flex-direction: column;
  color: var(--palette-color-default);
  border-radius: 0.5rem;
  transition: 0.2s;
  gap: 0.3rem;
  box-shadow: 0px 2px 10px 0px rgba(34, 40, 47, 0.1);
  box-shadow: 0px 0px 2px 0px rgba(34, 40, 47, 0.05);
}

.shift.completed {
  opacity: 60%;
}

.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.15rem;
}

.heading {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.02em;
  text-align: left;
  color: var(--palette-color-default-lighten-20);
  margin-bottom: 0.1rem;
}

.role {
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
}

.times {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default-lighten-30);
}

.progress {
  background-color: var(--palette-color-core-1-lighten-60);
}

.shift.completed .todos__progress-bar {
  background-color: var(--palette-color-default-lighten-50);
}

.action {
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default-lighten-30);
}

.shift.starting .action {
  color: var(--palette-color-brand-primary);
}

.shift.done .action {
  color: var(--palette-color-success);
}

.shift.overdue .action {
  color: var(--palette-color-danger);
}

.shift.done .todos__progress-bar {
  background-color: var(--palette-color-success);
}

.shift.done .progress {
  background-color: var(--palette-color-success-lighten-90);
}

.shift.overdue .todos__progress-bar {
  background-color: var(--palette-color-danger);
}

.heading-toggle-leave-active {
  transition: opacity 1s ease-in;
}

.heading-toggle-enter-active {
  transition: opacity 0.5s;
}

.heading-toggle-enter,
.heading-toggle-leave-to {
  opacity: 0;
}

.heading-toggle-leave,
.heading-toggle-enter-to {
  opacity: 1;
}
</style>
